import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import MarkdownRenderer from "./MarkdownRenderer";
import ThinkingRenderer from "./ThinkingRenderer";

/**
 * Typing indicator component that shows three animated dots
 * @param {boolean} compact - Whether to use compact styling for mobile
 */
const TypingIndicator = ({ compact = false }) => {
  return (
    <Box
      sx={{
        display: "flex",
        gap: compact ? 0.3 : 0.5,
        py: compact ? 0.5 : 1,
        alignItems: "center",
      }}
    >
      {[0, 1, 2].map((index) => (
        <Box
          key={index}
          sx={{
            width: compact ? 6 : 8,
            height: compact ? 6 : 8,
            borderRadius: "50%",
            bgcolor: "text.secondary",
            opacity: 0.7,
            animation: "pulse 1.5s infinite",
            animationDelay: `${index * 0.2}s`,
            "@keyframes pulse": {
              "0%, 100%": {
                transform: "scale(1)",
                opacity: 0.7,
              },
              "50%": {
                transform: "scale(1.2)",
                opacity: 1,
              },
            },
          }}
        />
      ))}
    </Box>
  );
};

/**
 * Helper function to process content and extract thinking sections
 */
const processThinkingContent = (content) => {
  if (!content)
    return {
      mainContent: "",
      thinkingSections: [],
      isActiveThinking: false,
      activeThinkingContent: "",
    };

  let mainContent = content;
  const thinkingSections = [];
  let activeThinkingContent = "";

  // Extract all thinking sections
  let currentIndex = 0;
  while (true) {
    // Find the next thinking section
    const thinkStart = mainContent.indexOf("<think>", currentIndex);
    if (thinkStart === -1) break; // No more thinking sections

    const thinkEnd = mainContent.indexOf("</think>", thinkStart);
    if (thinkEnd === -1) break; // Incomplete thinking section

    // Extract thinking content
    const thinkingContent = mainContent
      .substring(thinkStart + 7, thinkEnd)
      .trim();
    if (thinkingContent) {
      thinkingSections.push(thinkingContent);
    }

    // Remove this thinking section from the main content
    mainContent =
      mainContent.substring(0, thinkStart) +
      mainContent.substring(thinkEnd + 8);
  }

  // Check if there's an incomplete thinking section (we're actively receiving thinking content)
  const lastThinkStart = mainContent.indexOf("<think>");
  const isActiveThinking = lastThinkStart !== -1;

  // If there's an incomplete section, capture and remove it from the main content
  if (isActiveThinking) {
    activeThinkingContent = mainContent.substring(lastThinkStart + 7).trim();
    mainContent = mainContent.substring(0, lastThinkStart);
  }

  return {
    mainContent,
    thinkingSections,
    isActiveThinking,
    activeThinkingContent,
  };
};

/**
 * StreamingMessage component that handles streaming content with markdown rendering
 * @param {string} initialContent - The initial content to display
 * @param {function} onContentUpdate - Callback for content updates
 * @param {boolean} compact - Whether to use compact styling for mobile
 * @param {string} status - The current status message to display
 */
const StreamingMessage = forwardRef(
  ({ initialContent = "", onContentUpdate, compact = false, status }, ref) => {
    // State to hold the accumulated content
    const [content, setContent] = useState(initialContent);
    // Tracks if we've received any content yet
    const [hasContent, setHasContent] = useState(!!initialContent);
    // Tracks if we're still streaming
    const [isStreaming, setIsStreaming] = useState(true);
    // Track the cursor position
    const [cursorVisible, setCursorVisible] = useState(true);
    // Store content in a ref to ensure we can always retrieve the latest value
    const contentRef = React.useRef(initialContent);
    // State to track thinking sections (multiple)
    const [thinkingSections, setThinkingSections] = useState([]);
    // Track if we're currently in a thinking section
    const [isActiveThinking, setIsActiveThinking] = useState(false);
    // Content of the active thinking section
    const [activeThinkingContent, setActiveThinkingContent] = useState("");
    // Keep track of the full content including thinking
    const fullContentRef = React.useRef(initialContent);

    // Process initial content for thinking sections
    useEffect(() => {
      if (initialContent) {
        const {
          mainContent,
          thinkingSections,
          isActiveThinking,
          activeThinkingContent,
        } = processThinkingContent(initialContent);
        setThinkingSections(thinkingSections);
        setContent(mainContent);
        setIsActiveThinking(isActiveThinking);
        setActiveThinkingContent(activeThinkingContent);
        contentRef.current = mainContent;
        fullContentRef.current = initialContent;
      }
    }, [initialContent]);

    // Function to add new content chunks
    const addContent = (chunk) => {
      if (chunk) {
        // Update the full content first
        fullContentRef.current += chunk;

        // Process the full content to extract thinking sections
        const {
          mainContent,
          thinkingSections: newThinkingSections,
          isActiveThinking: newIsActiveThinking,
          activeThinkingContent: newActiveThinkingContent,
        } = processThinkingContent(fullContentRef.current);

        // Update thinking sections if changed
        if (newThinkingSections.length !== thinkingSections.length) {
          setThinkingSections(newThinkingSections);
        }

        // Update active thinking state
        setIsActiveThinking(newIsActiveThinking);
        setActiveThinkingContent(newActiveThinkingContent);

        // Update the main content
        setContent(mainContent);
        contentRef.current = mainContent;

        // Notify parent component about significant updates that might need scrolling
        if (
          onContentUpdate &&
          (chunk.includes("\n") ||
            chunk.includes("```") ||
            chunk.includes("#") ||
            chunk.length > 10 ||
            chunk.includes("</think>"))
        ) {
          onContentUpdate(mainContent);
        }

        if (!hasContent) {
          setHasContent(true);
        }
      }
    };

    // Function to signal when streaming is complete
    const completeStreaming = () => {
      // Make sure to preserve the content by setting it from the ref
      const finalContent = contentRef.current || "";

      // FORCE all streaming indicators to stop immediately
      setIsStreaming(false);
      setCursorVisible(false);
      setIsActiveThinking(false);
      setActiveThinkingContent("");

      // Update content with final value
      setContent(finalContent);

      // Do one final processing of thinking content
      const { mainContent, thinkingSections: finalThinkingSections } =
        processThinkingContent(fullContentRef.current);
      if (finalThinkingSections.length > 0) {
        setThinkingSections(finalThinkingSections);
        setContent(mainContent);
      }

      // Attempt to directly manipulate DOM to hide cursor elements as a failsafe
      try {
        const cursorElements = document.querySelectorAll(
          '[data-streaming-cursor="true"]',
        );
        cursorElements.forEach((el) => {
          el.style.display = "none";
          el.style.opacity = "0";
          el.style.visibility = "hidden"; // Add visibility for extra certainty
        });
      } catch (e) {
        console.error("Failed to directly hide cursor elements", e);
      }

      // Forcibly update component state after a minimal delay to ensure cursor is hidden
      setTimeout(() => {
        setIsStreaming(false);
        setCursorVisible(false);
      }, 10);

      // Add a final failsafe check after a longer delay
      setTimeout(() => {
        if (cursorVisible) {
          setCursorVisible(false);
        }
      }, 100);
    };

    // Function to get the current content
    const getContent = () => {
      // If we want the complete content including thinking, we should return fullContentRef
      return fullContentRef.current || "";
    };

    // Expose functions via the ref
    useImperativeHandle(ref, () => ({
      addContent,
      completeStreaming,
      getContent,
    }));

    // Blink the cursor effect
    useEffect(() => {
      if (!isStreaming) {
        // Ensure cursor is hidden when not streaming
        setCursorVisible(false);

        // Try to directly manipulate the DOM when streaming stops
        try {
          const cursorElements = document.querySelectorAll(
            '[data-streaming-cursor="true"]',
          );
          cursorElements.forEach((el) => {
            el.style.display = "none";
            el.style.opacity = "0";
            el.style.visibility = "hidden";
          });
        } catch (e) {
          console.error(
            "Failed to directly hide cursor elements in useEffect",
            e,
          );
        }

        return;
      }

      const interval = setInterval(() => {
        setCursorVisible((prev) => !prev);
      }, 500);

      // Clean up interval on unmount or when streaming stops
      return () => {
        clearInterval(interval);
        // Ensure cursor is hidden when cleaned up
        setCursorVisible(false);

        // Also try to hide cursor elements directly when cleaning up
        try {
          const cursorElements = document.querySelectorAll(
            '[data-streaming-cursor="true"]',
          );
          cursorElements.forEach((el) => {
            el.style.display = "none";
            el.style.opacity = "0";
            el.style.visibility = "hidden";
          });
        } catch (e) {
          console.error(
            "Failed to directly hide cursor elements in cleanup",
            e,
          );
        }
      };
    }, [isStreaming]);

    // Initialize contentRef with initialContent
    useEffect(() => {
      if (initialContent) {
        contentRef.current = initialContent;
        fullContentRef.current = initialContent;
      }
    }, [initialContent]);

    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          overflowWrap: "break-word",
          wordBreak: "break-word",
        }}
      >
        {/* Status message when streaming */}
        {isStreaming && status && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: compact ? 0.5 : 1,
            }}
          >
            <CircularProgress
              size={compact ? 12 : 16}
              sx={{ marginInlineEnd: compact ? 0.5 : 1 }}
            />
            <Typography
              variant={compact ? "caption" : "body2"}
              sx={{ fontStyle: "italic" }}
            >
              {status}
            </Typography>
          </Box>
        )}

        {/* Display previously completed thinking sections */}
        {thinkingSections.map((thinkingContent, index) => (
          <ThinkingRenderer
            key={`thinking-${index}`}
            content={thinkingContent}
            isStreaming={false}
            compact={compact}
          />
        ))}

        {/* Display currently active thinking section */}
        {isStreaming && isActiveThinking && (
          <ThinkingRenderer
            content={activeThinkingContent}
            isStreaming={true}
            compact={compact}
          />
        )}

        {/* Render main content */}
        <MarkdownRenderer content={content} compact={compact} />

        {/* Render blinking cursor or loading indicator */}
        {isStreaming && (
          <Box
            data-streaming-cursor="true"
            sx={{
              display: "inline-block",
              visibility: cursorVisible ? "visible" : "hidden",
              mt: 0.5,
            }}
          >
            {!hasContent ? (
              <TypingIndicator compact={compact} />
            ) : (
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: compact ? 7 : 10,
                  height: compact ? 17 : 20,
                  bgcolor: "text.primary",
                  animation: "blink 1s infinite",
                  verticalAlign: "text-bottom",
                  "@keyframes blink": {
                    "0%, 100%": {
                      opacity: 1,
                    },
                    "50%": {
                      opacity: 0,
                    },
                  },
                }}
              />
            )}
          </Box>
        )}
      </Box>
    );
  },
);

// Add display name for debugging
StreamingMessage.displayName = "StreamingMessage";

export default StreamingMessage;
